import {
  type PlatformScopeAnswer,
  type StudioEntitlement,
  studioEntitlementSchema,
} from "@mountaya/studio";

/**
 * ScopeAnswer is what the platform answers a person may use of the studio in one scope, as the
 * console read it: a membership's answer and the personal space's alike, the features as the wire
 * spells them.
 */
export interface ScopeAnswer {
  /**
   * How many itineraries the person may keep in the scope, or null where no ceiling applies. Left
   * out, the answer states no ceiling, as a membership never does: an organization's itineraries
   * are bounded by its agreement, which the platform's own refusal states. The personal space's
   * answer states one.
   */
  readonly itinerary_limit?: number | null | undefined;
  /** The features the answer lists, as the wire spells them. */
  readonly studio_entitlements: readonly string[];
}

/**
 * isStudioEntitlement reports whether a feature the wire names is one the studio's own vocabulary
 * names: the table `@mountaya/studio` publishes, held to the contract's enum on its side.
 */
const isStudioEntitlement = (value: string): value is StudioEntitlement =>
  studioEntitlementSchema.safeParse(value).success;

/**
 * studioAnswerOf reads an answer the console holds into the one the studio's viewer reads: the
 * ceiling as the platform stated it, where it stated one, and the features it lists that the
 * studio names. A feature
 * the contract gained before this application's studio knew it is dropped, which withholds an
 * affordance the studio could not draw anyway; nothing here adds one the answer does not list.
 *
 * @param answer - The answer as the console read it.
 * @returns The answer in the studio's vocabulary.
 */
export const studioAnswerOf = ({
  itinerary_limit,
  studio_entitlements,
}: ScopeAnswer): PlatformScopeAnswer => ({
  ...(itinerary_limit === undefined ? {} : { itinerary_limit }),
  studio_entitlements: studio_entitlements.filter(isStudioEntitlement),
});
